/* eslint-disable indent */
import { Fleet } from '@src/main/modules/parameters/domain/fleet-positions/fleet.interface';
import { Position } from '@src/main/modules/parameters/domain/fleet-positions/position.interface';
import { fleetPositionsRequestFromFleet } from '@src/main/modules/parameters/infrastructure/dto/updateFleetPositionsRequest';
import FleetAssignmentBody from '@src/main/modules/parameters/ui/fleet-assignment/components/FleetAssignmentBody';
import { useFleets } from '@src/main/modules/parameters/ui/fleet-positions/hooks/useFleets';
import { usePositions } from '@src/main/modules/parameters/ui/fleet-positions/hooks/usePositions';
import { useUpdateFleetPositions } from '@src/main/modules/parameters/ui/fleet-positions/hooks/useUpdateFleetPositions';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import { CustomConfirmDialog } from '@src/main/shared/ui/components/dialog/CustomConfirmDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import './FleetAssignment.scss';

interface ConfirmDialogState {
  visible: boolean;
  message?: string;
  header?: string;
  onAccept?: () => void;
}

const FleetAssignment: FC = () => {
  const { data: fleets = [], isLoading: loadingFleets } = useFleets();
  const { data: positions = [] } = usePositions();
  const updateFleetPositions = useUpdateFleetPositions();
  const { hasPermission } = useAuth();
  const hasEditPermissions = hasPermission(PERMISSIONS.PARAMETERS.FLEET_ASSIGNMENT.edit);
  const queryClient = useQueryClient();
  const toast = useToast();

  const [selectedFleet, setSelectedFleet] = useState<Fleet | null>(null);
  const [positionsToAdd, setPositionsToAdd] = useState<Position[]>([]);
  const [positionsToRemove, setPositionsToRemove] = useState<Position[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({ visible: false });

  const positionsNotAssigned = useMemo(() => {
    if (!selectedFleet || positions.length === 0) return [];
    return positions.filter(
      (p) => !selectedFleet.positions.some((sp) => sp.positionId === p.positionId),
    );
  }, [selectedFleet, positions]);

  const pushUniquePosition = (arr: Position[], item: Position) =>
    arr.some((p) => p.positionId === item.positionId) ? arr : [...arr, item];

  const removePosition = (item: Position) => {
    if (!selectedFleet) return;

    setSelectedFleet((prev) =>
      prev
        ? { ...prev, positions: prev.positions.filter((p) => p.positionId !== item.positionId) }
        : prev,
    );

    const original = fleets.find((f) => f.fleetId === selectedFleet.fleetId);
    const wasOriginallyAssigned = original?.positions.some(
      (sp) => sp.positionId === item.positionId,
    );

    if (wasOriginallyAssigned) {
      setPositionsToRemove((prev) => {
        const without = prev.filter((p) => p.positionId !== item.positionId);
        return [...without, item];
      });
      setPositionsToAdd((prev) => prev.filter((p) => p.positionId !== item.positionId));
    } else {
      setPositionsToAdd((prev) => prev.filter((p) => p.positionId !== item.positionId));
    }
  };

  const addPosition = (item: Position) => {
    if (!selectedFleet) return;

    setSelectedFleet((prev) =>
      prev ? { ...prev, positions: pushUniquePosition(prev.positions, item) } : prev,
    );

    const original = fleets.find((f) => f.fleetId === selectedFleet.fleetId);
    const wasOriginallyAssigned = original?.positions.some(
      (sp) => sp.positionId === item.positionId,
    );

    if (wasOriginallyAssigned) {
      setPositionsToRemove((prev) => prev.filter((p) => p.positionId !== item.positionId));
    } else {
      setPositionsToAdd((prev) => {
        const without = prev.filter((p) => p.positionId !== item.positionId);
        return [...without, item];
      });
    }
  };

  const resetFleet = () => {
    const original = fleets.find((f) => f.fleetId === selectedFleet?.fleetId);
    if (original) {
      setSelectedFleet(JSON.parse(JSON.stringify(original)));
      setPositionsToAdd([]);
      setPositionsToRemove([]);
    }
  };

  const openConfirmDialog = (message: string, header: string, onAccept: () => void) => {
    setConfirmDialog({ visible: true, message, header, onAccept });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFleetChange = (e: any) => {
    const fleet: Fleet = e.value;

    if (positionsToAdd.length > 0 || positionsToRemove.length > 0) {
      openConfirmDialog(
        // eslint-disable-next-line max-len
        'Tiene cambios sin guardar. ¿Seguro que desea cambiar de flota? Se perderán todos los cambios.',
        'Aviso',
        () => {
          setSelectedFleet(fleet);
          setPositionsToAdd([]);
          setPositionsToRemove([]);
        },
      );
    } else {
      setSelectedFleet(fleet);
    }
  };

  const cancelChanges = () => {
    if (!selectedFleet) return;

    if (positionsToAdd.length > 0 || positionsToRemove.length > 0) {
      openConfirmDialog('Tiene cambios sin guardar. ¿Seguro que desea cancelar?', 'Aviso', () =>
        resetFleet(),
      );
    } else {
      resetFleet();
    }
  };

  const saveChanges = () => {
    if (!selectedFleet) return;

    openConfirmDialog(`¿Desea guardar cambios en ${selectedFleet.fleet}?`, 'Aviso', async () => {
      updateFleetPositions.mutate(
        {
          id: selectedFleet.fleetId,
          data: fleetPositionsRequestFromFleet(selectedFleet),
        },
        {
          onSuccess: async () => {
            showSuccessMessage(
              toast,
              `Puestos de la flota ${selectedFleet.fleet} actualizados correctamente`,
            );
            await queryClient.invalidateQueries({ queryKey: ['fleet'] });
            await queryClient.invalidateQueries({ queryKey: ['position'] });
            setPositionsToAdd([]);
            setPositionsToRemove([]);
          },
          onError: (err) => showErrorMessage(toast, err),
        },
      );
    });
  };

  return (
    <ContentCard>
      <HeaderTitle title="Asignaciones Flota" />
      <FleetAssignmentBody
        fleets={fleets}
        selectedFleet={selectedFleet}
        positionsNotAssigned={positionsNotAssigned}
        positionsToAdd={positionsToAdd}
        positionsToRemove={positionsToRemove}
        hasEditPermissions={hasEditPermissions}
        loadingFleets={loadingFleets}
        handleFleetChange={handleFleetChange}
        addPosition={addPosition}
        removePosition={removePosition}
        saveChanges={saveChanges}
        cancelChanges={cancelChanges}
        updateFleetPositionsPending={updateFleetPositions.isPending}
      />
      <CustomConfirmDialog
        visible={confirmDialog.visible}
        message={confirmDialog.message}
        header={confirmDialog.header}
        onAccept={() => {
          setConfirmDialog({ visible: false });
          confirmDialog.onAccept?.();
        }}
        onReject={() => setConfirmDialog({ visible: false })}
      />
    </ContentCard>
  );
};

export default FleetAssignment;
